"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";

export default function Navbar() {
  const location = usePathname();
  const isAuthPage =
    location === "/login" ||
    location.startsWith("/register") ||
    location === "/forgot-password" ||
    location === "/register" ||
    location === "/teacher/register" ||
    location === "/admin/register";

  const { user, isLoading } = useAuth();

  function dashboardUrl(role: string) {
    if (role === "super_admin") return "/super-admin/dashboard";
    if (role === "country_admin") return "/country-admin";
    if (role === "teacher") return "/teacher";
    return "/student";
  }

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const navHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navHeight;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <Link
          href="/"
          className="flex items-center gap-3 pressable w-fit rounded-md focus-ring"
          data-testid="link-nav-home"
        >
          <img
            src="/fpl-logo.png"
            alt="Financial Literacy Program Logo"
            className="h-8 md:h-10 object-contain"
            data-testid="img-nav-logo"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-bold text-[15px]" data-testid="text-nav-title">
              Financial Literacy Program
            </div>
            <div className="text-xs text-muted-foreground" data-testid="text-nav-subtitle">
              INJAZ Al-Arab
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-4 md:gap-8">
          {!isAuthPage && (
            <>
              <nav className="hidden md:flex items-center gap-8 font-medium text-sm text-muted-foreground">
                <a
                  href="#why"
                  onClick={(e) => handleScroll(e, "why")}
                  className="link-underline hover:text-primary pressable"
                >
                  Why it matters
                </a>
                <a
                  href="#program"
                  onClick={(e) => handleScroll(e, "program")}
                  className="link-underline hover:text-primary pressable"
                >
                  What this program is
                </a>
                <a
                  href="#how-to-start"
                  onClick={(e) => handleScroll(e, "how-to-start")}
                  className="link-underline hover:text-primary pressable"
                >
                  How to start
                </a>
                <a
                  href="#who"
                  onClick={(e) => handleScroll(e, "who")}
                  className="link-underline hover:text-primary pressable"
                >
                  Who it's for
                </a>
              </nav>

              <div className="hidden sm:flex items-center gap-2">
                {isLoading ? null : user ? (
                  <Link href={dashboardUrl(user.role)} className="inline-flex">
                    <Button className="rounded-full px-6 font-semibold shadow-md hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/30 transition-all">
                      My Dashboard
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link href="/login" className="inline-flex">
                      <Button
                        variant="outline"
                        className="rounded-full font-semibold border-primary text-primary hover:bg-primary/10 hover:-translate-y-1 hover:shadow-md hover:shadow-primary/20 transition-all"
                      >
                        Login
                      </Button>
                    </Link>
                    <Link href="/register" className="inline-flex">
                      <Button className="rounded-full px-6 font-semibold shadow-md hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/30 transition-all">Register</Button>
                    </Link>
                  </>
                )}
              </div>

              <div className="md:hidden flex items-center">
                <Sheet>
                  <SheetTrigger
                    render={<Button variant="ghost" size="icon" className="md:hidden" />}
                  >
                    <Menu className="h-6 w-6" />
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                    <div className="flex flex-col gap-6 mt-10">
                      <nav className="flex flex-col gap-4 text-lg font-medium">
                        <a
                          href="#why"
                          onClick={(e) => handleScroll(e, "why")}
                          className="hover:text-primary"
                        >
                          Why it matters
                        </a>
                        <a
                          href="#program"
                          onClick={(e) => handleScroll(e, "program")}
                          className="hover:text-primary"
                        >
                          What this program is
                        </a>
                        <a
                          href="#how-to-start"
                          onClick={(e) => handleScroll(e, "how-to-start")}
                          className="hover:text-primary"
                        >
                          How to start
                        </a>
                        <a
                          href="#who"
                          onClick={(e) => handleScroll(e, "who")}
                          className="hover:text-primary"
                        >
                          Who it's for
                        </a>
                      </nav>
                      <div className="flex flex-col gap-3 mt-4">
                        {isLoading ? null : user ? (
                          <Link href={dashboardUrl(user.role)} className="w-full">
                            <Button className="w-full rounded-full font-semibold">My Dashboard</Button>
                          </Link>
                        ) : (
                          <>
                            <Link href="/login" className="w-full">
                              <Button variant="outline" className="w-full rounded-full font-semibold">Login</Button>
                            </Link>
                            <Link href="/register" className="w-full">
                              <Button className="w-full rounded-full font-semibold">Register</Button>
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
